import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { PublicShell } from "@/components/layout/PublicShell";
import { PortalShell } from "@/components/layout/PortalShell";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { hasAdminPermission } from "@/lib/adminPermissions";
import { api } from "@/lib/api";
import ModuleReaderPage from "@/pages/ModuleReaderPage";
import { AdminLoginPage, ApplyPage, LoginPage, ResetPasswordPage, SetupPasswordPage } from "@/pages/AuthPages";
import AdminAuditPage from "@/pages/AdminAuditPage";
import AdminManagementPage from "@/pages/AdminManagementPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import { AdminApplicationDetailPage, AdminDashboardPage, StudentPortalPage } from "@/pages/PortalPages";
import { AboutPage, ContactPage, HomePage, ModulesPage, PrivacyPage, TermsPage } from "@/pages/PublicPages";
import YocoPaymentResultPage from "@/pages/YocoPaymentResultPage";


const LoadingView = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#f8f4ec]">
    <div className="space-y-3 text-center">
      <p className="text-3xl font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="global-loading-title">Loading portal</p>
      <p className="text-sm text-slate-600" data-testid="global-loading-description">Preparing your secure session and public programme details...</p>
    </div>
  </div>
);

const STUDENT_ROLES = ["student"];
const ADMIN_ROLES = ["super_admin", "admin"];
const SUPER_ADMIN_ROLES = ["super_admin"];


const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
};


const SessionGuard = ({ roles, adminPermission, children, redirectTo }) => {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return <LoadingView />;
  }

  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  if (!session || !allowedRoles.includes(session.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  if (adminPermission && !hasAdminPermission(session, adminPermission)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};


const AppRoutes = ({ publicConfig, refreshPublicConfig }) => {
  const { session, logout, refreshSession, sessionLoading } = useAuth();
  const adminNavLinks = useMemo(() => [
    { to: "/admin/dashboard", label: "Applicants", testId: "portal-admin-dashboard-link-button" },
    ...(session?.can_manage_admins ? [{ to: "/admin/admins", label: "Admin management", testId: "portal-admin-management-link-button" }] : []),
    ...(hasAdminPermission(session, "full_access") ? [{ to: "/admin/audit", label: "Audit log", testId: "portal-admin-audit-link-button" }] : []),
    ...(hasAdminPermission(session, "full_access") ? [{ to: "/admin/settings", label: "Settings", testId: "portal-admin-settings-link-button" }] : []),
  ], [session]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const renderPublic = useCallback((PageComponent) => (
    <PublicShell publicConfig={publicConfig} session={session} onLogout={handleLogout}>
      <PageComponent publicConfig={publicConfig} />
    </PublicShell>
  ), [handleLogout, publicConfig, session]);

  if (!publicConfig && sessionLoading) {
    return <LoadingView />;
  }

  return (
    <Routes>
      <Route path="/" element={renderPublic(HomePage)} />
      <Route path="/about" element={renderPublic(AboutPage)} />
      <Route path="/modules" element={renderPublic(ModulesPage)} />
      <Route path="/apply" element={renderPublic(ApplyPage)} />
      <Route path="/login" element={renderPublic(LoginPage)} />
      <Route path="/contact" element={renderPublic(ContactPage)} />
      <Route path="/privacy" element={renderPublic(PrivacyPage)} />
      <Route path="/terms" element={renderPublic(TermsPage)} />
      <Route path="/setup" element={renderPublic(SetupPasswordPage)} />
      <Route path="/reset-password" element={renderPublic(ResetPasswordPage)} />
      <Route path="/admin/login" element={renderPublic(AdminLoginPage)} />
      <Route path="/payments/yoco/result" element={renderPublic(YocoPaymentResultPage)} />

      <Route
        path="/student"
        element={
          <SessionGuard roles={STUDENT_ROLES} redirectTo="/login">
            <PortalShell title="Student portal" eyebrow="Applicant / student" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig}>
              <StudentPortalPage session={session} publicConfig={publicConfig} refreshPublicConfig={refreshPublicConfig} />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/portal/modules/:moduleCode/read"
        element={
          <SessionGuard roles={STUDENT_ROLES} redirectTo="/login">
            <PortalShell title="Module reader" eyebrow="Applicant / student" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig}>
              <ModuleReaderPage />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <SessionGuard roles={ADMIN_ROLES} redirectTo="/admin/login">
            <PortalShell title={session?.admin_role === "super_admin" ? "Super admin dashboard" : "Admin dashboard"} eyebrow="Administration" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig} navLinks={adminNavLinks}>
              <AdminDashboardPage session={session} refreshSession={refreshSession} />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/admin/applications/:applicationId"
        element={
          <SessionGuard roles={ADMIN_ROLES} redirectTo="/admin/login">
            <PortalShell title="Application detail" eyebrow="Administration" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig} navLinks={adminNavLinks}>
              <AdminApplicationDetailPage session={session} />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/admin/admins"
        element={
          <SessionGuard roles={ADMIN_ROLES} adminPermission="manage_admins" redirectTo="/admin/dashboard">
            <PortalShell title="Admin management" eyebrow="Administration" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig} navLinks={adminNavLinks}>
              <AdminManagementPage session={session} />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/admin/audit"
        element={
          <SessionGuard roles={ADMIN_ROLES} adminPermission="full_access" redirectTo="/admin/dashboard">
            <PortalShell title="Audit history" eyebrow="Administration" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig} navLinks={adminNavLinks}>
              <AdminAuditPage />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <SessionGuard roles={SUPER_ADMIN_ROLES} adminPermission="full_access" redirectTo="/admin/dashboard">
            <PortalShell title="Super admin settings" eyebrow="Administration" session={session} onLogout={handleLogout} homeLink="/" publicConfig={publicConfig} navLinks={adminNavLinks}>
              <AdminSettingsPage />
            </PortalShell>
          </SessionGuard>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};


function App() {
  const [publicConfig, setPublicConfig] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const response = await api.get("/public/config");
      setPublicConfig(response.data);
    } catch (_error) {
      setPublicConfig(null);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!publicConfig) return;

    document.title = publicConfig.portal_name;

    const colors = publicConfig?.brand?.colors || {};
    const root = document.documentElement;
    root.style.setProperty("--brand-primary-dark", colors.primary_dark || "#2B2B2B");
    root.style.setProperty("--brand-secondary-dark", colors.secondary_dark || "#3A3A3A");
    root.style.setProperty("--brand-primary-orange", colors.primary_orange || "#F5A623");
    root.style.setProperty("--brand-secondary-red", colors.secondary_red || "#B3262D");
    root.style.setProperty("--brand-white", colors.white || "#FFFFFF");
    root.style.setProperty("--brand-offwhite", colors.offwhite || "#F7F5F1");

    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }
    favicon.href = publicConfig?.brand?.primary_logo_url;
  }, [publicConfig]);

  return (
    <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes publicConfig={publicConfig} refreshPublicConfig={loadConfig} />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}


export default App;
